/**
 * Cadastro de um item novo no catálogo.
 *
 * ## Por que é um formulário separado do editor
 *
 * O **padrão de acesso** só existe aqui: ele decide a natureza da pergunta
 * "quem entra", e é cadastro, não apresentação. O editor não o mostra.
 *
 * Misturar os dois formulários exigiria esconder metade dos campos conforme o
 * modo, e o resultado seria uma tela que ninguém entende de olhar.
 *
 * ## O grupo virou atalho, não exigência
 *
 * Até 19/10 o padrão A **exigia** o grupo aqui, e o vínculo era imutável
 * depois. O resultado foi nenhum item de padrão A no catálogo: ninguém cadastra
 * um agente com o GUID do grupo na mão, e quem tinha o GUID não era quem
 * cadastrava.
 *
 * Agora o campo é opcional e existe só para poupar uma volta quando o GUID já
 * está à mão. O lugar do vínculo é a aba **Grupos**, que entra pelo grupo,
 * mostra quantas pessoas ele libera e registra cada ligação no log.
 */
import { useEffect, useState } from 'react';
import { api, SessionError, goToSignIn } from '../api.js';
import { notify } from './Toast.jsx';
import { AreaPicker } from './AreaPicker.jsx';

const PATTERNS = [
  {
    id: 'A',
    label: 'A — grupo do Entra',
    hint: 'A plataforma sabe quem tem acesso, porque o grupo vem no token. Os grupos se ligam na aba Grupos.',
  },
  {
    id: 'B',
    label: 'B — licença na própria ferramenta',
    hint: 'Login é Microsoft, mas quem libera é a licença lá dentro. Copilot Studio, Power BI.',
  },
  {
    id: 'C',
    label: 'C — conta própria, fora do Entra',
    hint: 'A ferramenta tem cadastro dela. O que a plataforma oferece é a instrução de login.',
  },
];

/**
 * "Agente de Contratos" → "agente-de-contratos".
 *
 * Sugestão, não imposição: o campo continua editável. O identificador vira
 * parte de URL e é como a equipe fala do item em chamado — quem cadastra pode
 * querer algo mais curto que o nome.
 */
function slugify(text) {
  return text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);
}

export function NewItem({ onClose, onCreated }) {
  const [form, setForm] = useState({
    id: '',
    name: '',
    kind: 'AGENT',
    accessPattern: 'B',
    entraGroupId: '',
    targetUrl: '',
    ownerEmail: '',
    ownerName: '',
    category: '',
    description: '',
    shortName: '',
    initials: '',
    accessLabel: '',
    vendor: '',
    platformType: '',
    loginHint: '',
    status: 'ACTIVE',
    tags: '',
  });
  // Conjunto, não texto: fica fora do `form` pelo mesmo motivo dos links no
  // editor.
  const [areas, setAreas] = useState([]);
  // Enquanto ninguém digitou no campo de identificador, ele acompanha o nome.
  const [idTocado, setIdTocado] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const set = (campo) => (e) => setForm((f) => ({ ...f, [campo]: e.target.value }));

  function setName(e) {
    const name = e.target.value;
    setForm((f) => ({ ...f, name, id: idTocado ? f.id : slugify(name) }));
  }

  async function submit(e) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const empty = (v) => (v.trim() === '' ? null : v.trim());
      const r = await api.admin.createItem({
        id: form.id.trim(),
        name: form.name.trim(),
        kind: form.kind,
        accessPattern: form.accessPattern,
        // Plural, e omitido quando vazio: `.strict()` recusa campo
        // desconhecido, e `undefined` some do JSON — o que é diferente de
        // mandar lista vazia, que também passa mas diz "nenhum" de propósito.
        ...(form.accessPattern === 'A' && form.entraGroupId.trim()
          ? { entraGroupIds: [form.entraGroupId.trim()] }
          : {}),
        targetUrl: form.targetUrl.trim(),
        ownerEmail: form.ownerEmail.trim(),
        ownerName: empty(form.ownerName),
        category: empty(form.category),
        areas,
        description: empty(form.description),
        shortName: empty(form.shortName),
        initials: empty(form.initials),
        accessLabel: empty(form.accessLabel),
        vendor: empty(form.vendor),
        platformType: empty(form.platformType),
        loginHint: form.accessPattern === 'C' ? empty(form.loginHint) : null,
        status: form.status,
        tags: form.tags
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean),
      });
      notify(`"${r.item.name}" foi cadastrado.`);
      onCreated(r.item);
    } catch (err) {
      if (err instanceof SessionError) return goToSignIn();
      setError(err.message);
    } finally {
      setSaving(false);
    }
    return undefined;
  }

  const pattern = PATTERNS.find((p) => p.id === form.accessPattern);

  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="item-panel editor-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="novo-titulo"
        onClick={(e) => e.stopPropagation()}
      >
        <form className="editor-form" onSubmit={submit}>
          <header className="panel-header editor-head">
            <h2 id="novo-titulo" className="panel-title">
              Novo item no catálogo
            </h2>
            <button className="close-button" type="button" onClick={onClose} aria-label="Fechar">
              ×
            </button>
          </header>

          <div className="panel-body editor-body">
            <div className="field-row">
              <Field label="Nome" htmlFor="n-name">
                <input
                  id="n-name"
                  className="input"
                  value={form.name}
                  onChange={setName}
                  required
                  maxLength={200}
                />
              </Field>
              <Field label="Tipo" htmlFor="n-kind">
                <select id="n-kind" className="input" value={form.kind} onChange={set('kind')}>
                  <option value="AGENT">Agente</option>
                  <option value="SYSTEM">Sistema</option>
                </select>
              </Field>
            </div>

            <Field
              label="Identificador"
              htmlFor="n-id"
              hint="Vira parte da URL e é como a equipe fala do item. Minúsculas, números e hífen"
            >
              <input
                id="n-id"
                className="input"
                value={form.id}
                onChange={(e) => {
                  setIdTocado(true);
                  setForm((f) => ({ ...f, id: e.target.value }));
                }}
                required
                pattern="[a-z0-9]+(-[a-z0-9]+)*"
                maxLength={64}
              />
            </Field>

            {/* O campo que só existe aqui. Ver o cabeçalho. */}
            <div className="locked-note">
              <strong>O padrão de acesso só é definido agora.</strong> Depois de criado, ele não é
              editável pela tela — mudar o padrão muda a natureza de quem entra.
            </div>

            <Field label="Padrão de acesso" htmlFor="n-pattern" hint={pattern?.hint}>
              <select
                id="n-pattern"
                className="input"
                value={form.accessPattern}
                onChange={set('accessPattern')}
              >
                {PATTERNS.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.label}
                  </option>
                ))}
              </select>
            </Field>

            {form.accessPattern === 'A' && (
              <Field
                label="Grupo do Entra"
                htmlFor="n-group"
                hint="Opcional. Se deixar em branco, ligue os grupos depois na aba Grupos — lá dá para ver quantas pessoas cada um libera"
              >
                <input
                  id="n-group"
                  className="input"
                  value={form.entraGroupId}
                  onChange={set('entraGroupId')}
                  maxLength={64}
                  /* O Object ID, não o nome. O placeholder antigo sugeria
                     `SEC-AIHUB-…`, e um nome gravado aqui criaria um item que
                     parece liberado e não abre para ninguém: a comparação com
                     o token é por GUID. */
                  placeholder="00000000-0000-0000-0000-000000000000"
                />
              </Field>
            )}

            {form.accessPattern === 'C' && (
              <Field
                label="Instrução de login"
                htmlFor="n-hint"
                hint="Aparece no painel do item, para quem não tem conta"
              >
                <input
                  id="n-hint"
                  className="input"
                  value={form.loginHint}
                  onChange={set('loginHint')}
                  maxLength={500}
                />
              </Field>
            )}

            <Field label="Endereço principal" htmlFor="n-url" hint="Precisa começar com https://">
              <input
                id="n-url"
                className="input"
                type="url"
                value={form.targetUrl}
                onChange={set('targetUrl')}
                required
                maxLength={500}
                placeholder="https://…"
              />
            </Field>

            <div className="field-row">
              <Field label="Responsável" htmlFor="n-owner-name">
                <input
                  id="n-owner-name"
                  className="input"
                  value={form.ownerName}
                  onChange={set('ownerName')}
                  maxLength={200}
                />
              </Field>
              <Field
                label="E-mail do responsável"
                htmlFor="n-owner-email"
                hint="É quem aprova o pedido"
              >
                <input
                  id="n-owner-email"
                  className="input"
                  type="email"
                  value={form.ownerEmail}
                  onChange={set('ownerEmail')}
                  required
                  maxLength={200}
                />
              </Field>
            </div>

            <Field
              label="Descrição"
              htmlFor="n-desc"
              hint={`Aparece no cartão e no detalhe · ${form.description.length}/4000`}
            >
              <textarea
                id="n-desc"
                className="textarea"
                rows={5}
                value={form.description}
                onChange={set('description')}
                maxLength={4000}
              />
            </Field>

            <Field
              label="Aparece nas abas"
              hint="Onde o item se mostra no portfólio. Deixando vazio, entra só na área responsável abaixo"
            >
              <AreaPicker value={areas} onChange={setAreas} />
            </Field>

            <div className="field-row">
              <Field
                label="Área responsável"
                htmlFor="n-cat"
                hint="Só informativo, aparece no detalhe"
              >
                <input
                  id="n-cat"
                  className="input"
                  value={form.category}
                  onChange={set('category')}
                  maxLength={80}
                />
              </Field>
              <Field label="Iniciais" htmlFor="n-init" hint="Até 4 letras, sem logotipo">
                <input
                  id="n-init"
                  className="input"
                  value={form.initials}
                  onChange={set('initials')}
                  maxLength={4}
                />
              </Field>
            </div>

            <div className="field-row">
              <Field label="Fornecedor" htmlFor="n-vendor">
                <input
                  id="n-vendor"
                  className="input"
                  value={form.vendor}
                  onChange={set('vendor')}
                  maxLength={120}
                />
              </Field>
              <Field label="Plataforma" htmlFor="n-plat">
                <input
                  id="n-plat"
                  className="input"
                  value={form.platformType}
                  onChange={set('platformType')}
                  maxLength={120}
                />
              </Field>
            </div>

            <Field label="Público" htmlFor="n-access" hint="Descritivo, em linguagem humana">
              <input
                id="n-access"
                className="input"
                value={form.accessLabel}
                onChange={set('accessLabel')}
                maxLength={200}
              />
            </Field>

            <Field
              label="Etiquetas"
              htmlFor="n-tags"
              hint="Separadas por vírgula. Aparecem no detalhe e entram na busca"
            >
              <input id="n-tags" className="input" value={form.tags} onChange={set('tags')} />
            </Field>

            <Field
              label="Estado"
              htmlFor="n-status"
              hint="Só “Ativo” avisa as pessoas de que o item existe"
            >
              <select id="n-status" className="input" value={form.status} onChange={set('status')}>
                <option value="ACTIVE">Ativo</option>
                <option value="COMING_SOON">Em breve</option>
                <option value="INACTIVE">Inativo (some da tela)</option>
              </select>
            </Field>

            {error && (
              <p className="error-note" role="alert">
                {error}
              </p>
            )}
          </div>

          <footer className="panel-actions">
            <button type="button" className="button ghost" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="button primary" disabled={saving}>
              {saving ? 'Cadastrando…' : 'Cadastrar'}
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
}

function Field({ label, htmlFor, hint, children }) {
  // Sem `htmlFor` o rótulo não é `<label>` — ver o mesmo comentário em
  // `pages/Admin.jsx`.
  const Rotulo = htmlFor ? 'label' : 'span';
  return (
    <div className="field">
      <Rotulo className="field-label" htmlFor={htmlFor}>
        {label}
        {hint && <span className="field-hint">{hint}</span>}
      </Rotulo>
      {children}
    </div>
  );
}

export default NewItem;
